import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  MapPin,
  Check,
  Loader2,
  ChevronDown,
  AlertCircle,
  X,
  Search,
  Phone,
  Edit,
} from "lucide-react";
import { regionOptions, locationsByRegion } from "../../utils/data";
import { toast } from "react-toastify";
import branchService from "../../requests/branches/BranchService";

const ValidationError = ({ message }) => (
  <motion.p
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex gap-1 items-center mt-1 text-sm text-red-500"
  >
    <AlertCircle className="w-4 h-4" />
    {message}
  </motion.p>
);

const BranchManagementModal = ({ isOpen, onClose, onBranchesUpdated }) => {
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState({
    id: null,
    name: "",
    region: "",
    location: "",
    customLocation: "",
    district: "",
    suburb: "",
    landmark: "",
    gpsAddress: "",
    googleMapsLink: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phoneNumber: "",
    region: "",
    location: "",
    customLocation: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchBranches();
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentBranch.region) {
      const locations = locationsByRegion[currentBranch.region] || [];
      setAvailableLocations([...locations, "Other"]);
    }
  }, [currentBranch.region]);

  const fetchBranches = async () => {
    setIsLoadingBranches(true);
    try {
      const response = await branchService.getBranches();
      const { data } = response;
      if (data?.status_code === "000" && data?.in_error === false) {
        setBranches(data?.data || []);
      } else {
        console.log(data?.reason || "Failed to fetch branches");
      }
    } catch (error) {
      console.log(error?.response?.reason || "Failed to fetch branches");
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const validateBranchName = (name) => {
    if (!name.trim()) return "Branch name is required";
    if (name.length < 3) return "Branch name must be at least 3 characters";
    if (name.length > 50) return "Branch name must be less than 50 characters";
    return "";
  };

  const validatePhoneNumber = (number) => {
    if (!number) return "Phone number is required";
    if (number.length !== 9) return "Phone number must be exactly 9 digits";
    if (!/^\d+$/.test(number)) return "Phone number must contain only digits";
    return "";
  };

  const validateLocation = () => {
    if (!currentBranch.region) return "Region is required";
    if (!currentBranch.location && !isCustomLocation)
      return "Location is required";
    if (isCustomLocation && !currentBranch.customLocation.trim())
      return "Custom location is required";
    return "";
  };

  const handleRegionChange = (region) => {
    setCurrentBranch((prev) => ({
      ...prev,
      region,
      location: "",
      customLocation: "",
    }));
    setIsCustomLocation(false);
    setErrors((prev) => ({ ...prev, region: "", location: "" }));
  };

  const handleLocationChange = (location) => {
    setCurrentBranch((prev) => ({
      ...prev,
      location,
      customLocation: "",
    }));
    setIsCustomLocation(location === "Other");
    setErrors((prev) => ({ ...prev, location: "", customLocation: "" }));
  };

  const validateBranchForm = () => {
    const newErrors = {
      name: validateBranchName(currentBranch.name),
      phoneNumber: validatePhoneNumber(currentBranch.phoneNumber),
      location: validateLocation(),
    };

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !Object.values(newErrors).some(Boolean);
  };

  const applyBranch = async () => {
    if (!validateBranchForm()) return;

    try {
      setIsLoading(true);

      const branchPayload = {
        name: currentBranch.name,
        region: currentBranch.region,
        location: isCustomLocation
          ? currentBranch.customLocation
          : currentBranch.location,
        district: currentBranch.district || "",
        suburb: currentBranch.suburb || "",
        landmark: currentBranch.landmark || "",
        gpsAddress: currentBranch.gpsAddress || "",
        google_url: currentBranch.googleMapsLink || "",
        phone_number: currentBranch.phoneNumber.startsWith("233")
          ? currentBranch.phoneNumber
          : `233${currentBranch.phoneNumber}`,
      };

      let response;
      if (isEditing) {
        // Update existing branch
        response = await branchService.updateBranch(currentBranch.branch_slug, branchPayload);
        if (
          response.status &&
          response.data?.status_code === "000" &&
          !response.data?.in_error
        ) {
          toast.success("Branch updated successfully");
        } else {
          throw new Error(response.message || "Failed to update branch");
        }
      } else {
        // Add new branch
        const payload = {
          branches: [branchPayload],
        };
        response = await branchService.addBranch(payload);
        if (
          response.status &&
          response.data?.status_code === "001" &&
          !response.data?.in_error
        ) {
          toast.success("Branch added successfully");
        } else {
          throw new Error(response.message || "Failed to add branch");
        }
      }

      fetchBranches();
      onBranchesUpdated && onBranchesUpdated();
      resetForm();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'adding'} branch:`, error);
      toast.error(error.message || `Failed to ${isEditing ? 'update' : 'add'} branch`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentBranch({
      id: null,
      name: "",
      region: "",
      location: "",
      customLocation: "",
      district: "",
      suburb: "",
      landmark: "",
      gpsAddress: "",
      googleMapsLink: "",
      phoneNumber: "",
    });
    setIsEditing(false);
    setIsCustomLocation(false);
    setErrors({});
  };

  const handleEditBranch = (branch) => {
    const isLocationCustom = !locationsByRegion[branch.region]?.includes(
      branch.location
    );

    setCurrentBranch({
      ...branch,
      phoneNumber: branch.phone_number?.startsWith("233")
        ? branch.phone_number.replace("233", "")
        : branch.phone_number || "",
      customLocation: isLocationCustom ? branch.location : "",
      location: isLocationCustom ? "Other" : branch.location,
      gpsAddress: branch.gps_address || "",
      googleMapsLink: branch.google_maps_link || "",
    });
    setIsEditing(true);
    setIsCustomLocation(isLocationCustom);
  };

  const filteredBranches = branches.filter((branch) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      branch.name.toLowerCase().includes(searchTermLower) ||
      branch.location.toLowerCase().includes(searchTermLower) ||
      branch.region.toLowerCase().includes(searchTermLower)
    );
  });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-gradient-to-br backdrop-blur-sm from-black/60 via-black/50 to-black/60"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-7xl max-h-[95vh] bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-[#FF6C6C] via-[#FF8A8A] to-[#FF6C6C] p-6">
            <div className="absolute inset-0 backdrop-blur-sm bg-white/5"></div>
            <div className="flex relative justify-between items-center">
              <div className="flex gap-4 items-center">
                <div className="p-3 rounded-2xl border shadow-lg backdrop-blur-sm bg-white/20 border-white/30">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Branch Management
                  </h2>
                  <p className="text-sm font-medium text-white/90">
                    Add, edit, and manage your business locations
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-3 rounded-2xl border backdrop-blur-sm transition-all duration-200 text-white/80 hover:bg-white/20 hover:text-white hover:scale-105 border-white/20"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex h-[calc(95vh-140px)]">
            {/* Left Side - Form */}
            <div className="overflow-y-auto p-8 w-1/2 bg-gradient-to-br to-white border-r border-gray-100 from-gray-50/50">
              <div className="space-y-6">
                <div className="flex gap-3 items-center mb-6">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF8A8A]/10 border border-[#FF6C6C]/20">
                    <MapPin className="w-6 h-6 text-[#FF6C6C]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {isEditing ? "Edit Branch" : "Add New Branch"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {isEditing ? "Update branch information" : "Create a new business location"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block mb-2 text-sm font-semibold text-gray-800">
                      Branch Name
                      <span className="text-[#FF6C6C] ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={currentBranch.name}
                        onChange={(e) => {
                          setCurrentBranch((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }));
                          setErrors((prev) => ({ ...prev, name: "" }));
                        }}
                        className={`w-full px-4 py-3.5 text-base border-2 ${
                          errors.name
                            ? "border-red-300 bg-red-50/50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md`}
                        placeholder="Enter branch name"
                      />
                    </div>
                    <AnimatePresence>
                      {errors.name && <ValidationError message={errors.name} />}
                    </AnimatePresence>
                  </div>

                  <div className="col-span-2">
                    <label className="block mb-2 text-sm font-semibold text-gray-800">
                      Phone Number
                      <span className="text-[#FF6C6C] ml-1">*</span>
                    </label>
                    <div className="relative">
                      <div className="flex absolute inset-y-0 left-0 items-center pl-4 pointer-events-none">
                        <span className="font-medium text-gray-600">233</span>
                      </div>
                      <input
                        type="text"
                        value={currentBranch.phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 9) {
                            setCurrentBranch((prev) => ({
                              ...prev,
                              phoneNumber: value,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              phoneNumber: "",
                            }));
                          }
                        }}
                        className={`w-full pl-14 pr-4 py-3.5 text-base border-2 ${
                          errors.phoneNumber
                            ? "border-red-300 bg-red-50/50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md`}
                        placeholder="Enter phone number"
                        required
                      />
                    </div>
                    <p className="mt-2 text-xs font-medium text-gray-600">
                      Enter 9 digits without the prefix
                    </p>
                    <AnimatePresence>
                      {errors.phoneNumber && (
                        <ValidationError message={errors.phoneNumber} />
                      )}
                    </AnimatePresence>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-800">
                      Region
                      <span className="text-[#FF6C6C] ml-1">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={currentBranch.region}
                        onChange={(e) => handleRegionChange(e.target.value)}
                        className={`w-full px-4 py-3.5 text-base border-2 ${
                          errors.region
                            ? "border-red-300 bg-red-50/50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 appearance-none cursor-pointer shadow-sm hover:shadow-md`}
                      >
                        <option value="">Select Region</option>
                        {regionOptions.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-2 rounded-lg bg-[#FF6C6C]/10">
                        <ChevronDown className="w-4 h-4 text-[#FF6C6C]" />
                      </div>
                    </div>
                    <AnimatePresence>
                      {errors.region && (
                        <ValidationError message={errors.region} />
                      )}
                    </AnimatePresence>
                  </div>

                  {currentBranch.region && (
                    <div>
                      <label className="block mb-2 text-sm font-semibold text-gray-800">
                        Location
                        <span className="text-[#FF6C6C] ml-1">*</span>
                      </label>
                      <div className="relative">
                        <select
                          value={currentBranch.location}
                          onChange={(e) =>
                            handleLocationChange(e.target.value)
                          }
                          className={`w-full px-4 py-3.5 text-base border-2 ${
                            errors.location
                              ? "border-red-300 bg-red-50/50"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          } rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 appearance-none cursor-pointer shadow-sm hover:shadow-md`}
                        >
                          <option value="">Select Location</option>
                          {availableLocations.map((location) => (
                            <option key={location} value={location}>
                              {location}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-2 rounded-lg bg-[#FF6C6C]/10">
                          <ChevronDown className="w-4 h-4 text-[#FF6C6C]" />
                        </div>
                      </div>
                      <AnimatePresence>
                        {errors.location && (
                          <ValidationError message={errors.location} />
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {isCustomLocation && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4"
                  >
                    <label className="block mb-2 text-sm font-semibold text-gray-800">
                      Custom Location
                      <span className="text-[#FF6C6C] ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={currentBranch.customLocation}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          customLocation: e.target.value,
                        }))
                      }
                      className={`w-full px-4 py-3.5 text-base border-2 ${
                        errors.customLocation
                          ? "border-red-300 bg-red-50/50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      } rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md`}
                      placeholder="Enter custom location"
                    />
                    <AnimatePresence>
                      {errors.customLocation && (
                        <ValidationError message={errors.customLocation} />
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}

                {/* Optional Fields */}
                <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                  <div className="flex col-span-2 gap-2 items-center mb-2">
                    <div className="w-1 h-6 bg-gradient-to-b from-[#FF6C6C] to-[#FF8A8A] rounded-full"></div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Additional Details
                    </h3>
                    <span className="text-xs font-medium text-gray-500">(Optional)</span>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      District
                    </label>
                    <input
                      type="text"
                      value={currentBranch.district}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          district: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3.5 text-base border-2 border-gray-200 bg-white hover:border-gray-300 rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Enter district name"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      Suburb
                    </label>
                    <input
                      type="text"
                      value={currentBranch.suburb}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          suburb: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3.5 text-base border-2 border-gray-200 bg-white hover:border-gray-300 rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Enter suburb name"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      Landmark
                    </label>
                    <input
                      type="text"
                      value={currentBranch.landmark}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          landmark: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3.5 text-base border-2 border-gray-200 bg-white hover:border-gray-300 rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Enter a nearby landmark"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      GPS Address
                    </label>
                    <input
                      type="text"
                      value={currentBranch.gpsAddress}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          gpsAddress: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3.5 text-base border-2 border-gray-200 bg-white hover:border-gray-300 rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Enter GPS address"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      Google Maps Link
                    </label>
                    <input
                      type="url"
                      value={currentBranch.googleMapsLink}
                      onChange={(e) =>
                        setCurrentBranch((prev) => ({
                          ...prev,
                          googleMapsLink: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3.5 text-base border-2 border-gray-200 bg-white hover:border-gray-300 rounded-2xl focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="https://maps.google.com/..."
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={applyBranch}
                    disabled={
                      !currentBranch.name ||
                      !currentBranch.region ||
                      (!currentBranch.location &&
                        !currentBranch.customLocation) ||
                      !currentBranch.phoneNumber ||
                      isLoading
                    }
                    className="flex-1 py-4 bg-gradient-to-r from-[#FF6C6C] via-[#FF8A8A] to-[#FF6C6C] text-white rounded-2xl flex items-center justify-center gap-3 hover:from-[#E55A59] hover:via-[#FF7575] hover:to-[#E55A59] transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-xl group font-semibold text-base"
                  >
                    <Check className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                    {isLoading ? (
                      <div className="flex gap-2 items-center">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Adding...
                      </div>
                    ) : (
                      isEditing ? "Update Branch" : "Add Branch"
                    )}
                  </motion.button>

                  {isEditing && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={resetForm}
                      className="px-8 py-4 text-base font-semibold text-gray-700 bg-gray-100 rounded-2xl border border-gray-200 transition-all duration-300 hover:bg-gray-200 hover:text-gray-900"
                    >
                      Cancel
                    </motion.button>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Branch List */}
            <div className="overflow-y-auto p-8 w-1/2 bg-gradient-to-br from-white to-gray-50/30">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Your Branches
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Manage your business locations
                    </p>
                  </div>
                  <div className="px-4 py-2.5 bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF8A8A]/10 rounded-2xl border border-[#FF6C6C]/20">
                    <span className="text-sm font-bold text-[#FF6C6C]">
                      {branches.length} {branches.length === 1 ? "Branch" : "Branches"}
                    </span>
                  </div>
                </div>

                <div className="relative">
                  <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search branches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="py-4 pr-4 pl-12 w-full bg-white rounded-2xl border-2 border-gray-200 shadow-sm transition-all duration-300 focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] hover:border-gray-300 hover:shadow-md"
                  />
                </div>

                {isLoadingBranches ? (
                  <div className="flex justify-center items-center p-12">
                    <div className="flex flex-col gap-4 items-center">
                      <Loader2 className="w-10 h-10 animate-spin text-[#FF6C6C]" />
                      <p className="font-medium text-gray-600">Loading branches...</p>
                    </div>
                  </div>
                ) : filteredBranches.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="p-6 mx-auto mb-6 w-20 h-20 bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF8A8A]/10 rounded-2xl border border-[#FF6C6C]/20">
                      <Building2 className="w-10 h-10 text-[#FF6C6C]" />
                    </div>
                    <h4 className="mb-3 text-xl font-bold text-gray-900">
                      No Branches Found
                    </h4>
                    <p className="mx-auto max-w-sm text-gray-600">
                      {searchTerm ? "No branches match your search criteria" : "Add your first branch to start managing your business locations"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredBranches.map((branch, index) => (
                      <motion.div
                        key={branch.branch_slug}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="group p-5 bg-white rounded-2xl border-2 border-gray-100 hover:border-[#FF6C6C]/30 transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-[#FF6C6C]/10"
                      >
                        <div className="flex gap-4 items-start">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF8A8A]/10 flex items-center justify-center shrink-0 border border-[#FF6C6C]/20 group-hover:scale-110 transition-transform duration-300">
                            <Building2 className="w-6 h-6 text-[#FF6C6C]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h6 className="mb-2 text-lg font-bold text-gray-900 truncate">
                                  {branch.name}
                                </h6>
                                <div className="space-y-2 text-sm">
                                  <div className="flex gap-2 items-center text-gray-600">
                                    <MapPin className="w-4 h-4 text-[#FF6C6C] shrink-0" />
                                    <span className="font-medium truncate">
                                      {branch.location}, {branch.region}
                                    </span>
                                  </div>
                                  {branch.phone_number && (
                                    <div className="flex gap-2 items-center text-gray-600">
                                      <Phone className="w-4 h-4 text-[#FF6C6C] shrink-0" />
                                      <span className="font-medium truncate">
                                        {branch.phone_number}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEditBranch(branch)}
                                  className="p-3 text-gray-400 rounded-xl hover:bg-[#FF6C6C]/10 hover:text-[#FF6C6C] transition-all duration-300 border border-transparent hover:border-[#FF6C6C]/20"
                                  title="Edit branch"
                                >
                                  <Edit className="w-5 h-5" />
                                </motion.button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BranchManagementModal;
